import type { ApiError } from '@dentai/shared';

export type UiLocale = 'it' | 'en' | 'ar';

const localized: Record<UiLocale, Record<string, string>> = {
  en: {
    INVALID_CREDENTIALS: 'Email or password is incorrect.', EMAIL_ALREADY_EXISTS: 'An account already exists for this email.',
    UNAUTHORIZED: 'Your session has expired. Please sign in again.', FORBIDDEN: 'You do not have permission to perform this action.',
    VALIDATION_ERROR: 'Check the entered information and try again.', EMAIL_VERIFICATION_REQUIRED: 'Verify your email before accessing DentAI.',
    EMAIL_VERIFICATION_INVALID: 'This verification link is invalid or has already been used.', EMAIL_VERIFICATION_EXPIRED: 'This verification link has expired.',
    RATE_LIMITED: 'Too many attempts. Please wait and try again.', NOT_FOUND: 'The requested item could not be found.',
    CONFLICT: 'This action conflicts with the current state. Refresh and try again.', TENANT_VIOLATION: 'The requested item could not be found.',
    SERVICE_UNAVAILABLE: 'The service is temporarily unavailable. Please try again shortly.', NETWORK_ERROR: 'Check your connection and try again.',
    INTERNAL_ERROR: 'Something went wrong. Please try again.', SERVER_ERROR: 'Something went wrong. Please try again.', BILLING_ERROR: 'Billing is temporarily unavailable. Please try again.',
    ENTITLEMENT_ERROR: 'Your current plan does not include this feature.', COMMUNICATION_ERROR: 'The communication could not be completed.',
    INBOUND_COMMUNICATION_ERROR: 'The incoming message could not be processed.', LUNA_UNAVAILABLE: 'Luna is unavailable. The conversation remains queued for human review.',
    DRAFT_APPROVAL_ERROR: 'The reply could not be approved. Refresh and try again.', EMAIL_DELIVERY_ERROR: 'The email could not be delivered. Review its delivery status.',
    UNKNOWN: 'Something went wrong. Please try again.',
  },
  it: {
    INVALID_CREDENTIALS: 'Email o password non corretti.', EMAIL_ALREADY_EXISTS: 'Esiste già un account con questa email.',
    UNAUTHORIZED: 'La sessione è scaduta. Accedi di nuovo.', FORBIDDEN: 'Non hai i permessi per eseguire questa operazione.',
    VALIDATION_ERROR: 'Controlla i dati inseriti e riprova.', EMAIL_VERIFICATION_REQUIRED: 'Verifica la tua email prima di accedere a DentAI.',
    EMAIL_VERIFICATION_INVALID: 'Il link di verifica non è valido oppure è già stato usato.', EMAIL_VERIFICATION_EXPIRED: 'Il link di verifica è scaduto.',
    RATE_LIMITED: 'Troppi tentativi. Attendi e riprova.', NOT_FOUND: 'L’elemento richiesto non è stato trovato.',
    CONFLICT: 'L’operazione non è compatibile con lo stato attuale. Aggiorna e riprova.', TENANT_VIOLATION: 'L’elemento richiesto non è stato trovato.',
    SERVICE_UNAVAILABLE: 'Il servizio non è temporaneamente disponibile. Riprova tra poco.', NETWORK_ERROR: 'Controlla la connessione e riprova.',
    INTERNAL_ERROR: 'Si è verificato un problema. Riprova.', SERVER_ERROR: 'Si è verificato un problema. Riprova.', BILLING_ERROR: 'La fatturazione non è temporaneamente disponibile. Riprova.',
    ENTITLEMENT_ERROR: 'Il tuo piano attuale non include questa funzionalità.', COMMUNICATION_ERROR: 'Non è stato possibile completare la comunicazione.',
    INBOUND_COMMUNICATION_ERROR: 'Non è stato possibile elaborare il messaggio in arrivo.', LUNA_UNAVAILABLE: 'Luna non è disponibile. La conversazione resta in coda per la revisione umana.',
    DRAFT_APPROVAL_ERROR: 'Non è stato possibile approvare la risposta. Aggiorna e riprova.', EMAIL_DELIVERY_ERROR: 'Non è stato possibile consegnare l’email. Controlla lo stato di invio.',
    UNKNOWN: 'Si è verificato un problema. Riprova.',
  },
  ar: {
    INVALID_CREDENTIALS: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.', EMAIL_ALREADY_EXISTS: 'يوجد حساب مسجل بهذا البريد الإلكتروني.',
    UNAUTHORIZED: 'انتهت جلستك. يرجى تسجيل الدخول مجددًا.', FORBIDDEN: 'ليست لديك صلاحية لتنفيذ هذا الإجراء.',
    VALIDATION_ERROR: 'تحقق من البيانات المدخلة وحاول مرة أخرى.', EMAIL_VERIFICATION_REQUIRED: 'تحقق من بريدك الإلكتروني قبل الدخول إلى DentAI.',
    EMAIL_VERIFICATION_INVALID: 'رابط التحقق غير صالح أو سبق استخدامه.', EMAIL_VERIFICATION_EXPIRED: 'انتهت صلاحية رابط التحقق.',
    RATE_LIMITED: 'محاولات كثيرة جدًا. انتظر ثم حاول مرة أخرى.', NOT_FOUND: 'تعذر العثور على العنصر المطلوب.',
    CONFLICT: 'يتعارض هذا الإجراء مع الحالة الحالية. حدّث الصفحة وحاول مجددًا.', TENANT_VIOLATION: 'تعذر العثور على العنصر المطلوب.',
    SERVICE_UNAVAILABLE: 'الخدمة غير متاحة مؤقتًا. حاول مرة أخرى بعد قليل.', NETWORK_ERROR: 'تحقق من اتصالك وحاول مرة أخرى.',
    INTERNAL_ERROR: 'حدث خطأ. حاول مرة أخرى.', SERVER_ERROR: 'حدث خطأ. حاول مرة أخرى.', BILLING_ERROR: 'الفوترة غير متاحة مؤقتًا. حاول مرة أخرى.',
    ENTITLEMENT_ERROR: 'خطتك الحالية لا تشمل هذه الميزة.', COMMUNICATION_ERROR: 'تعذر إكمال عملية التواصل.',
    INBOUND_COMMUNICATION_ERROR: 'تعذرت معالجة الرسالة الواردة.', LUNA_UNAVAILABLE: 'Luna غير متاحة. ستبقى المحادثة في انتظار المراجعة البشرية.',
    DRAFT_APPROVAL_ERROR: 'تعذر اعتماد الرد. حدّث الصفحة وحاول مجددًا.', EMAIL_DELIVERY_ERROR: 'تعذر تسليم البريد الإلكتروني. راجع حالة الإرسال.',
    UNKNOWN: 'حدث خطأ. حاول مرة أخرى.',
  },
};

export function currentLocale(): UiLocale {
  if (typeof localStorage === 'undefined') return 'en';
  const value = localStorage.getItem('dentai_locale');
  return value === 'it' || value === 'ar' ? value : 'en';
}

function semanticCode(error: Pick<ApiError, 'code' | 'message'>): string {
  const code = String(error.code);
  if (localized.en[code]) return code;
  if (/billing|stripe|subscription/i.test(code)) return 'BILLING_ERROR';
  if (/entitlement|capability|plan/i.test(code)) return 'ENTITLEMENT_ERROR';
  if (/luna|copilot|ai_provider/i.test(code)) return 'LUNA_UNAVAILABLE';
  if (/draft|approval/i.test(code)) return 'DRAFT_APPROVAL_ERROR';
  if (/resend|email_delivery|provider/i.test(code)) return 'EMAIL_DELIVERY_ERROR';
  if (/inbound|webhook/i.test(code)) return 'INBOUND_COMMUNICATION_ERROR';
  if (/communication|messaging/i.test(code)) return 'COMMUNICATION_ERROR';
  return 'UNKNOWN';
}

export function localizeErrorCode(code: string, locale: UiLocale): string {
  return localized[locale][code] ?? localized[locale].UNKNOWN!;
}

export function localizeRetryAfter(seconds: number, locale: UiLocale = currentLocale()): string {
  const duration = new Intl.NumberFormat(locale).format(seconds);
  return locale === 'it' ? ` Riprova tra ${duration} secondi.` : locale === 'ar' ? ` حاول مجددًا بعد ${duration} ثانية.` : ` Try again in ${duration} seconds.`;
}

export function localizeApiError(error: ApiError, locale: UiLocale = currentLocale()): ApiError {
  const semantic = semanticCode(error);
  // Validation and policy errors are intentionally produced by our own API and
  // are safe to show. Keeping their text avoids hiding the one action an
  // operator needs to take behind a generic "Something went wrong" message.
  // Unexpected server failures remain redacted by the API error handler.
  const preserveSafeServerMessage = ['VALIDATION_ERROR', 'FORBIDDEN', 'CONFLICT'].includes(semantic)
    && Boolean(error.message?.trim());
  const message = preserveSafeServerMessage ? error.message : localizeErrorCode(semantic, locale);
  return {
    ...error,
    message,
    ...(error.details
      ? { details: error.details.map((detail) => ({ field: detail.field, message: detail.message })) }
      : {}),
  };
}

export function clientError(code: 'NETWORK_ERROR' | 'SERVICE_UNAVAILABLE', locale: UiLocale = currentLocale()): ApiError {
  return { code: code, message: localizeErrorCode(code, locale) };
}
